import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import {
    itemsKey,
    getConfigurableFields,
    buildApplyRecommendedPatch,
    buildResetPatch,
    applyPatchToItems,
} from '../../lib/editFieldsConfig';

// Cog drawer for edit forms (editProduct, editConvention, ...): lets a
// customer admin choose which fields their organisation sees. Config is the
// customer Mixed map <entityType>Items (1 = hidden), same pattern as
// journalItems. Toggles are generated from fields.js — no per-field
// duplication — and saves are mirrored into userStore.customer so the open
// edit drawer updates live. Required fields cannot be hidden.
@observer
class FieldsSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            configurableFields: getConfigurableFields(props.entityType),
            saving: false,
        };
    }

    mirrorToUserCustomer = (patchBody) => {
        const { entityType } = this.props;
        const { userStore } = this.props.stores;
        const { customer } = userStore;
        const key = itemsKey(entityType);
        const newItems = applyPatchToItems(customer[key] || {}, patchBody, entityType);
        userStore.updateObjectKeyValue('customer', key, newItems);
    }

    saveItems = async (patchBody) => {
        const { userStore } = this.props.stores;
        const { customer } = userStore;
        if (!customer.id || Object.keys(patchBody).length < 1) {
            return;
        }
        this.setState({ saving: true });
        const response = await util.fetchApi(`/api/customers/${customer.id}`, { method: 'PATCH' }, {
            id: customer.id,
            ...patchBody,
        });
        if (response.status >= 200 && response.status < 300) {
            this.mirrorToUserCustomer(patchBody);
        }
        this.setState({ saving: false });
    }

    toggleField = (field) => {
        if (field.isRequired) {
            return;
        }
        const { entityType } = this.props;
        const { userStore } = this.props.stores;
        const { customer } = userStore;
        const key = itemsKey(entityType);
        const isHidden = (customer[key] || {})[field.name] === 1;
        this.saveItems({ [`${key}.${field.name}`]: isHidden ? 0 : 1 });
    }

    applyRecommended = () => {
        const { entityType } = this.props;
        this.saveItems(buildApplyRecommendedPatch(entityType));
    }

    resetAll = () => {
        const { entityType } = this.props;
        const { userStore } = this.props.stores;
        const { customer } = userStore;
        this.saveItems(buildResetPatch(customer, entityType));
    }

    render() {
        const { entityType } = this.props;
        const { configurableFields, saving } = this.state;
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        const items = customer[itemsKey(entityType)] || {};
        const hasRecommended = configurableFields.some(f => f.isRecommended);

        const sectionKeys = [];
        configurableFields.forEach(f => {
            if (!sectionKeys.includes(f.sectionKey)) {
                sectionKeys.push(f.sectionKey);
            }
        });

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        <i class='fa-solid fa-gear me-2' />
                        <Text id='fields-settings.title'>Field settings</Text>
                    </div>
                    <div class='px-3 mb-3 text-body-secondary'>
                        <Text id='fields-settings.help'>Choose which fields your organization sees in this form. Hiding a field never deletes any data.</Text>
                    </div>
                    <div class='px-3 mb-3 d-flex flex-row flex-wrap gap-2 align-items-center'>
                        {hasRecommended && <button
                            type='button'
                            class='btn btn-sm btn-primary rounded-pill'
                            disabled={saving}
                            onClick={this.applyRecommended}
                        >
                            <i class='fa-solid fa-wand-magic-sparkles me-1' />
                            <Text id='fields-settings.apply-recommended'>Apply recommended fields</Text>
                        </button>}
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-secondary rounded-pill'
                            disabled={saving}
                            onClick={this.resetAll}
                        >
                            <i class='fa-solid fa-rotate-left me-1' />
                            <Text id='fields-settings.reset'>Show all fields</Text>
                        </button>
                        {saving && <span class='text-body-secondary'>
                            <i class='fa-solid fa-spinner fa-spin me-1' />
                            <Text id='fields-settings.saving'>Saving...</Text>
                        </span>}
                    </div>

                    {sectionKeys.map(sectionKey => {
                        const sectionFields = configurableFields.filter(f => f.sectionKey === sectionKey);
                        return (
                            <div class='px-3 mb-3' key={sectionKey}>
                                <div class='fw-bold mb-1'>{sectionFields[0].sectionTitle}</div>
                                <div class='list-group'>
                                    {sectionFields.map(field => {
                                        const isHidden = items[field.name] === 1;
                                        return (
                                            <label class='list-group-item d-flex flex-row align-items-center gap-2' key={field.name}>
                                                <input
                                                    class='form-check-input flex-shrink-0 m-0'
                                                    type='checkbox'
                                                    checked={!isHidden}
                                                    disabled={field.isRequired || saving}
                                                    onChange={() => this.toggleField(field)}
                                                />
                                                <span class={isHidden ? 'text-body-secondary' : ''}>{field.title}</span>
                                                {field.isRequired && <span class='badge bg-secondary ms-auto'>
                                                    <Text id='fields-settings.required'>Required</Text>
                                                </span>}
                                                {!field.isRequired && field.isRecommended && <span class='badge bg-info ms-auto'>
                                                    <Text id='fields-settings.recommended'>Recommended</Text>
                                                </span>}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default FieldsSettings;
